import { useParams } from 'react-router';
import { PageHeaderWithBackButton } from '../../../components/common';
import { Row, Spin } from 'antd';
import { Form, SubmitButton } from '../../../components/common/form';
import { useFetchCompanyDetails } from '../../../services/Company/useFetchCompanyDetails';
import FormFields from '../../../components/FormFields';
import { FormikHelpers } from 'formik';
import { Message } from 'fave-ui';
import { toolRedirect } from '../../../services/http';
import useSetBreadCrumbParams from '../../../hooks/useSetBreadCrumbParams';
import { useFetchCompanyOutlets } from '../../../services/Company/useFetchCompanyOutlets';
import { useUserContext } from '../../../contexts/UserContext';
import LockPage from '../../../components/LockPage';
import { useState } from 'react';
import { useUobPointRedemptionFormBuilder } from './helpers/useUOBPointRedemptionFormBuilder';
import { useCreateUobPointRedemption } from '../../../services/UOBPointRedemption/useCreateUOBPointRedemption';
import { useUpdateUobPointRedemption } from '../../../services/UOBPointRedemption/useUpdateUOBPointRedemption';
import { useFetchUobPointRedemptionDetailsViaMerchantConnect } from '../../../services/UOBPointRedemption/useFetchUOBPointRedemptionDetails';

const UOBPointRedemptionForm = () => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { mode, id, uob_point_redemption_id } = useParams();
  const companyId = String(id);
  const uobPointRedemptionId = String(uob_point_redemption_id);
  const isEditMode = mode === 'edit';

  const { getPageSettings } = useUserContext();
  const partnerSettings = getPageSettings('merchant_connect');
  const canShowUobPointRedemption = !!partnerSettings?.show;
  const canUpdateUobPointRedemption = canShowUobPointRedemption;
  const disableFormFields = !canUpdateUobPointRedemption;
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false);

  const { data: companyDetails, isFetching: isFetchingCompanyDetails } =
    useFetchCompanyDetails({
      company_id: companyId,
    });

  useSetBreadCrumbParams(
    [{ label: String(companyDetails?.name), key: companyId }],
    !!companyDetails,
  );

  const pageTitle = `${isEditMode ? 'Edit' : 'Create'} UOB Point Redemption (POS Terminal)`;
  const subTitle = !isFetchingCompanyDetails && (
    <span>
      Configure UOB Point Redemption for <strong>{companyDetails?.name}</strong>
    </span>
  );

  const { data: companyOutlets, isFetching: isFetchingCompanyOutlets } =
    useFetchCompanyOutlets({
      company_id: companyId,
    });

  const {
    data: uobPointRedemptionDetails,
    isFetching: isFetchingUobPointRedemptionDetails,
  } = useFetchUobPointRedemptionDetailsViaMerchantConnect(
    {
      uob_point_redemption_id: uobPointRedemptionId,
    },
    isEditMode,
  );

  const { formFields, formSchema, initialValues } =
    useUobPointRedemptionFormBuilder({
      companyOutlets,
      uobPointRedemptionDetails,
    });

  const { mutate: createUobPointRedemption, isLoading: isLoadingCreate } =
    useCreateUobPointRedemption();
  const { mutate: updateUobPointRedemption, isLoading: isLoadingUpdate } =
    useUpdateUobPointRedemption();

  const isFormLoading =
    isFetchingCompanyDetails ||
    isFetchingCompanyOutlets ||
    isLoadingCreate ||
    isLoadingUpdate ||
    isFetchingUobPointRedemptionDetails;

  const handleFormSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>,
  ) => {
    const formData = new FormData();

    const payload = {
      ...values,
    };

    const handleSuccess = (message: string) => {
      setDisabledSubmitButton(true);
      Message.success({ content: message }).then(() =>
        toolRedirect(
          `/admin/merchant_connect/merchants/${companyId}/uob_point_redemptions`,
        ),
      );
    };

    const appendMcProductProductDetailsFormData = (
      key: string,
      value: string | number | (string | number)[],
    ) => {
      if (Array.isArray(value)) {
        value.forEach(outletId =>
          formData.append(
            `mc_product[product_details][${key}][]`,
            String(outletId),
          ),
        );
      } else {
        formData.append(
          `mc_product[product_details][${key}]`,
          value != null ? String(value) : '',
        );
      }
    };

    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key as keyof typeof payload] as unknown as string;
        appendMcProductProductDetailsFormData(key, value);
      }
    }

    if (isEditMode) {
      updateUobPointRedemption(
        { mc_product: formData, uob_point_redemption_id: uobPointRedemptionId },
        {
          onSuccess: () =>
            handleSuccess('Successfully updated UOB Point Redemption'),
          onSettled: () => setSubmitting(false),
        },
      );
    } else {
      formData.append('mc_product[company_id]', companyId);
      formData.append('mc_product[product_type]', 'UOBPointRedemption');

      createUobPointRedemption(
        { mc_product: formData },
        {
          onSuccess: () =>
            handleSuccess('Successfully created UOB Point Redemption'),
          onSettled: () => setSubmitting(false),
        },
      );
    }
  };

  return (
    <>
      {canShowUobPointRedemption ? (
        <>
          <PageHeaderWithBackButton title={pageTitle} subTitle={subTitle} />
          <Spin spinning={isFormLoading}>
            <Form
              initialValues={initialValues}
              onSubmit={handleFormSubmit}
              validationSchema={formSchema}
              validateOnMount
              validateOnChange
              validateOnSchemaChange
            >
              <div className="flex justify-center">
                <div className="max-w-[750px]">
                  <Row gutter={24}>
                    <FormFields
                      formFields={formFields}
                      disableFormFields={disableFormFields}
                    />
                  </Row>
                  <div className={'flex justify-end'}>
                    <SubmitButton
                      disableIfNotValid
                      isDisabled={disableFormFields || disabledSubmitButton}
                      loading={isLoadingCreate || isLoadingUpdate}
                    >
                      {(_, isSubmitting) =>
                        isSubmitting ? 'Saving...' : 'Submit'
                      }
                    </SubmitButton>
                  </div>
                </div>
              </div>
            </Form>
          </Spin>
        </>
      ) : (
        <LockPage />
      )}
    </>
  );
};

export default UOBPointRedemptionForm;
